/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { FormMode, isCreateFormMode, isEditableFormMode, isEditFormMode, isViewFormMode } from '@gh/core-ui';
import { merge$ } from '@gh/rx';
import { map$, mapTo$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { ContainerState } from '../container-state';
import { queryAll } from './entity/entity.utils';
import { State } from './observable/state';

export type StateBasedFormQueries<T> = {
  dirty: TransformFn<T, boolean>;
  formMode: TransformFn<T, FormMode>;
};

export class ImmutablePageState<T> implements ContainerState<T> {
  changes: Observable<void>;
  dirty$: Observable<boolean>;
  mode$: Observable<FormMode>;
  editable$: Observable<boolean>;
  createMode$: Observable<boolean>;
  viewMode$: Observable<boolean>;
  editMode$: Observable<boolean>;

  private _mode: FormMode = FormMode.CreateMode;
  private _dirty = false;

  constructor(private state: State<T>, private queries: StateBasedFormQueries<T>) {
    const dirty$ = state.select(queries.dirty);
    const formMode$ = state.select(queries.formMode);

    dirty$.subscribe((dirty) => this._dirty = dirty);
    formMode$.subscribe((formMode) => this._mode = formMode);

    this.dirty$ = dirty$;
    this.mode$ = formMode$;
    this.editable$ = formMode$.pipe(map$(isEditableFormMode));
    this.createMode$ = formMode$.pipe(map$(isCreateFormMode));
    this.viewMode$ = formMode$.pipe(map$(isViewFormMode));
    this.editMode$ = formMode$.pipe(map$(isEditFormMode));
    this.changes = merge$(dirty$, formMode$).pipe(mapTo$(void 0));
  }

  get submitValue(): T {
    return this.state.project(queryAll);
  }

  get dirty(): boolean {
    return this._dirty;
  }

  set mode(_: FormMode) {
    console.warn('ImmutablePageState.mode setter was deprecated: implement mode support in the state instead');
  }

  get mode(): FormMode {
    return this._mode;
  }

  get createMode(): boolean {
    return this.mode === FormMode.CreateMode;
  }

  get editMode(): boolean {
    return this.mode === FormMode.EditMode;
  }

  get editable(): boolean {
    return this.createMode || this.editMode;
  }

  get viewMode(): boolean {
    return this.mode === FormMode.ViewMode;
  }

  get invalid(): boolean {
    return false;
  }

  get pending(): boolean {
    return false;
  }

  build(...args: any[]): void {
    // nothing to do
  }
}
